import { preparePhoto, photoMessage, PHOTO_ACCEPT, MAX_REQUEST_PHOTO_BYTES } from '../lib/photoUpload';
import { useEffect, useState } from "react";
import { submitEcoBainToJotform } from "../lib/jotformSubmit";
import { useLanguage } from "../context/LanguageContext";
import { clearAiPreviewOriginalPhoto, getAiPreviewOriginalPhoto } from "../lib/aiPreviewPhotoStore";
import { getLeadAttribution, markConfirmedLead } from "../lib/leadAttribution";

function PhotoThumbnail({ file }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  if (!src) return null;

  return (
    <img
      src={src}
      alt={file.name}
      width="64"
      height="64"
      loading="lazy"
      className="h-16 w-16 shrink-0 rounded-xl border-2 border-[#66C95E]/45 object-cover shadow-sm dark:border-[#66C95E]/40"
    />
  );
}

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",

    // Adresse détaillée (obligatoire)
    street: "",
    city: "",
    province: "", // UI = "Secteur" (Jotform "State")
    postalCode: "",

    // (optionnel) on garde address en string pour compat / logs / email
    address: "",

    email: "",
    phone: "",
    message: "",
  });

  const [photoError, setPhotoError] = useState("");
  const [isPreparingPhotos, setIsPreparingPhotos] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'error' | 'success' | null
  const [submitMessage, setSubmitMessage] = useState("");
  const [aiPreviewPhotoStatus, setAiPreviewPhotoStatus] = useState("");
  const [company, setCompany] = useState(""); // honeypot

  const { t, language } = useLanguage();
  const isEN = language === "en";

  const fieldBase =
    "w-full max-w-full min-w-0 rounded-2xl border px-4 py-3 text-[15px] outline-none transition " +
    "bg-white text-slate-900 placeholder:text-slate-400 " +
    "dark:bg-white/5 dark:text-white dark:placeholder:text-white/50 " +
    "border-slate-200/80 dark:border-white/10 " +
    "focus:ring-2 focus:ring-[#66C95E]/35 focus:border-[#66C95E]/60";

  const labels = {
    name: isEN ? "First name, last name" : "Prénom, Nom",
    workAddress: isEN ? "Work address" : "Adresse des travaux",
    street: isEN ? "Street address" : "Numéro civique, nom de rue, appartement",
    city: isEN ? "City" : "Ville",
    sector: isEN ? "Neighborhood / area (optional)" : "Quartier / secteur (optionnel)",
    postal: isEN ? "Postal code" : "Code postal",
    postalPlaceholder: "A1A 1A1",
    optional: isEN ? "(optional)" : "(optionnel)",
    removePhoto: isEN ? "Remove" : "Retirer",
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const normalizePostal = (value) => {
    const v = (value || "").toUpperCase().replace(/[^A-Z0-9 ]/g, "");
    const compact = v.replace(/\s+/g, "");
    if (compact.length <= 3) return compact;
    return `${compact.slice(0, 3)} ${compact.slice(3, 6)}`.trim();
  };

  const handlePostalChange = (e) => {
    const next = normalizePostal(e.target.value);
    setFormData((prev) => ({ ...prev, postalCode: next }));
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setIsPreparingPhotos(true);
    setPhotoError('');
    const next = [...photos];
    const errors = [];
    try {
      for (const file of files) {
        try {
          const prepared = await preparePhoto(file);
          if (next.some(p => p.name === prepared.name && p.lastModified === prepared.lastModified && p.size === prepared.size)) continue;
          if (next.reduce((sum, p) => sum + p.size, 0) + prepared.size > MAX_REQUEST_PHOTO_BYTES) throw new Error('total');
          next.push(prepared);
        } catch (err) {
          errors.push(file.name + ' : ' + (photoMessage(language, err.message) || photoMessage(language, 'preparation')));
        }
      }
      setPhotos(next);
      setPhotoError(errors.join(' '));
    } finally { setIsPreparingPhotos(false); }
  };

  const removePhoto = (index) => setPhotos((prev) => prev.filter((_, i) => i !== index));

  useEffect(() => {
    let isMounted = true;

    const attachAiPreviewPhoto = async () => {
      const original = await getAiPreviewOriginalPhoto();
      if (!isMounted || !original) {
        if (isMounted && new URLSearchParams(window.location.search).has('ai-preview-photo')) setPhotoError(photoMessage(language, 'storage'));
        return;
      }
      let file;
      try { file = await preparePhoto(original); }
      catch (err) { if (isMounted) setPhotoError(photoMessage(language, err.message) || photoMessage(language, 'preparation')); return; }
      if (!isMounted) return;

      setPhotos((prev) => {
        const key = `${file.name}_${file.size}_${file.lastModified}`;
        const alreadyAdded = prev.some((photo) => `${photo.name}_${photo.size}_${photo.lastModified}` === key);
        return alreadyAdded || prev.length ? prev : [file];
      });
      setAiPreviewPhotoStatus(t("form.aiPreviewPhotoRecovered"));
    };

    attachAiPreviewPhoto();

    return () => {
      isMounted = false;
    };
  }, [t, language]);

  const buildAddressString = () => {
    const parts = [
      (formData.street || "").trim(),
      (formData.city || "").trim(),
      (formData.province || "").trim(),
      (formData.postalCode || "").trim(),
    ].filter(Boolean);

    return parts.join(", ");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || isPreparingPhotos) return;

    if (company.trim() !== "") {
      setSubmitStatus("error");
      setSubmitMessage("Soumission bloquée (spam détecté).");
      return;
    }

    const form = e.currentTarget;
    try {
      if (form && typeof form.checkValidity === "function" && !form.checkValidity()) {
        form.reportValidity();
        setSubmitStatus("error");
        setSubmitMessage("Merci de compléter les champs obligatoires.");
        return;
      }
    } catch (err) {}

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");

    const addressString = buildAddressString();

    try {


      const result = await submitEcoBainToJotform({
        name: (formData.name || "").trim(),
        street: (formData.street || "").trim(),
        city: (formData.city || "").trim(),
        province: (formData.province || "").trim(),
        postalCode: (formData.postalCode || "").trim(),
        address: addressString,
        email: (formData.email || "").trim(),
        phone: (formData.phone || "").trim(),
        message: (formData.message || "").trim(),
        attribution: getLeadAttribution(),
        photoFiles: photos && photos.length ? photos : [],
        honeypot: company,
        language,
      });



      // IMPORTANT: plus de faux succès
      if (result && result.ok) {
        markConfirmedLead();
        setSubmitStatus("success");
        setSubmitMessage("Demande envoyée ✅");
        await clearAiPreviewOriginalPhoto();
        setAiPreviewPhotoStatus("");
        setPhotos([]);
        setFormData({
          name: "",
          street: "",
          city: "",
          province: "",
          postalCode: "",
          address: "",
          email: "",
          phone: "",
          message: "",
        });
        setTimeout(() => window.location.assign("/merci-devis.html"), 400);
      } else {
        setSubmitStatus("error");
        setSubmitMessage(result?.error || "Erreur d'envoi. Réessaie.");
      }
    } catch (err) {

      setSubmitStatus("error");
      setSubmitMessage(photoMessage(language, 'network'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section relative max-w-full overflow-x-hidden bg-[#071423] py-16 dark:bg-transparent sm:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
        <div className="grid min-w-0 max-w-full items-start gap-10 lg:grid-cols-2">
          {/* LEFT */}
<div className="min-w-0 max-w-full space-y-6">
  <p className="text-xs font-semibold tracking-[0.22em] text-[#66C95E]">
    {t("form.sectionTag")}
  </p>

  <h2 className="text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl text-white">
    {t("form.title")}
  </h2>

  <p className="max-w-xl text-white dark:text-white/70">
    {t("form.subtitle")}
  </p>

  <div className="space-y-4 text-base">
    <a
      className="flex items-center gap-4 text-white dark:text-white/80 hover:underline"
      href={`tel:${t("nav.phone")}`}
    >
      <span className="grid h-10 w-10 place-items-center rounded-2xl bg-[#66C95E]/12 text-[#66C95E]">
        <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden="true">
          <path
            d="M22 16.9v3a2 2 0 0 1-2.18 2A19.8 19.8 0 0 1 3.11 5.18 2 2 0 0 1 5.1 3h3a2 2 0 0 1 2 1.72c.12.86.3 1.7.54 2.5a2 2 0 0 1-.45 2.11L9.1 10.4a16 16 0 0 0 4.5 4.5l1.07-1.07a2 2 0 0 1 2.11-.45c.8.24 1.64.42 2.5.54A2 2 0 0 1 22 16.9Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>{t("nav.phone")}</span>
    </a>
    <a
      className="flex items-center gap-4 text-white dark:text-white/80 hover:underline"
      href="mailto:info@eco-bain.ca"
    >
      <span className="grid h-10 w-10 place-items-center rounded-2xl bg-[#66C95E]/12 text-[#66C95E]">
        <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden="true">
          <path
            d="M4 6h16v12H4V6Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
          <path
            d="m4 7 8 6 8-6"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <span>info@eco-bain.ca</span>
    </a>
  </div>
</div>

          {/* RIGHT / FORM */}
          <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-200/70 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5 sm:rounded-3xl sm:p-8">
            <form className="contact-form max-w-full min-w-0 space-y-4 overflow-hidden" onSubmit={handleSubmit}>
              {/* HONEYPOT */}
              <input
                type="text"
                name="website"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                autoComplete="new-password"
                tabIndex={-1}
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", opacity: 0 }}
              />

              {/* Nom (1 champ) */}
              <div>
                <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {isEN ? "Name" : "Votre nom"} <span className="text-[#66C95E]">*</span>
                </label>
                <input
                  className={fieldBase}
                  id="contact-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  onInput={handleChange}
                  required
                  autoComplete="name"
                  placeholder={labels.name}
                />
              </div>

              {/* Adresse détaillée (OBLIGATOIRE) */}
              <fieldset className="m-0 min-w-0 border-0 p-0">
                <legend className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {labels.workAddress} <span className="text-[#66C95E]">*</span>
                </legend>

                <div className="grid min-w-0 gap-3">
                  <label htmlFor="contact-street" className="sr-only">{labels.street}</label>
                  <input
                    className={fieldBase}
                    id="contact-street"
                    type="text"
                    name="street"
                    value={formData.street}
                    onChange={handleChange}
                    onInput={handleChange}
                    required
                    placeholder={labels.street}
                    autoComplete="address-line1"
                  />

                  <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-city" className="sr-only">{labels.city}</label>
                      <input
                        className={fieldBase}
                        id="contact-city"
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        onInput={handleChange}
                        required
                        placeholder={labels.city}
                        autoComplete="address-level2"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-sector" className="sr-only">{labels.sector}</label>
                      <input
                        className={fieldBase}
                        id="contact-sector"
                        type="text"
                        name="province"
                        value={formData.province}
                        onChange={handleChange}
                        onInput={handleChange}
                        placeholder={labels.sector}
                        autoComplete="address-level1"
                      />
                    </div>
                  </div>
                  <label htmlFor="contact-postal" className="sr-only">{labels.postal}</label>
                  <input
                    className={fieldBase}
                    id="contact-postal"
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handlePostalChange}
                    onInput={handlePostalChange}
                    required
                    placeholder={labels.postalPlaceholder}
                    autoComplete="postal-code"
                    pattern="[A-Za-z][0-9][A-Za-z][ ]?[0-9][A-Za-z][0-9]"
                    title={isEN ? "Format: A1A 1A1" : "Format : A1A 1A1"}
                  />
                </div>
              </fieldset>

              {/* Email */}
              <div>
                <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {t("form.email")} <span className="text-[#66C95E]">*</span>
                </label>
                <input
                  className={fieldBase}
                  id="contact-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onInput={handleChange}
                  required
                  autoComplete="email"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label htmlFor="contact-phone" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {t("form.phone")} <span className="text-[#66C95E]">*</span>
                </label>
                <input
                  className={fieldBase}
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  onInput={handleChange}
                  required
                  autoComplete="tel"
                />
              </div>

              {/* Projet (optionnel) */}
              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {t("form.message")} <span className="text-slate-400 dark:text-white/40">{labels.optional}</span>
                </label>
                <textarea
                  className={`${fieldBase} min-h-[120px] resize-none`}
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  onInput={handleChange}
                  placeholder={t("form.messagePlaceholder")}
                />
              </div>

              {/* Photos (optionnel) */}
              <div>
                <label htmlFor="contact-photos" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-white/80">
                  {t("form.photo")} <span className="text-slate-400 dark:text-white/40">{labels.optional}</span>
                </label>

                <div className="max-w-full overflow-hidden rounded-2xl border border-dashed border-slate-300/70 bg-white/60 p-3 text-sm text-slate-600 dark:border-white/15 dark:bg-white/5 dark:text-white/60 sm:p-4">
                  <input
                    className="block w-full max-w-full min-w-0 overflow-hidden text-ellipsis text-xs file:mr-3 file:rounded-xl file:border-0 file:bg-[#66C95E]/15 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-[#2f7f2a] dark:file:text-[#66C95E] sm:text-sm sm:file:px-4 sm:file:text-sm"
                    id="contact-photos"
                    type="file"
                    accept={PHOTO_ACCEPT}
                    disabled={isSubmitting || isPreparingPhotos}
                    multiple
                    onChange={handlePhotoChange}
                  />

                  {isPreparingPhotos && <p role="status" className="mt-2">{photoMessage(language, 'preparing')}</p>}
                  {photoError && <p role="alert" className="mt-2 break-words text-red-600 dark:text-red-300">{photoError}</p>}
                  {photos && photos.length > 0 ? (
                    <div className="mt-3 space-y-2">
                      {aiPreviewPhotoStatus && (
                        <p className="flex items-center gap-2 rounded-xl border border-[#66C95E]/30 bg-[#66C95E]/15 px-3 py-2.5 text-xs font-semibold leading-relaxed text-[#246b22] dark:border-[#66C95E]/35 dark:bg-[#66C95E]/10 dark:text-[#7bdf73]">
                          <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 shrink-0" aria-hidden="true">
                            <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>{aiPreviewPhotoStatus}</span>
                        </p>
                      )}
                      {photos.map((p, i) => (
                        <div key={i} className="grid max-w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-xl bg-white/70 p-2 dark:bg-white/[0.04]">
                          <PhotoThumbnail file={p} />
                          <div className="min-w-0 truncate">
                            <span className="font-medium text-slate-700 dark:text-white/80">
                              {t("form.photoAdded")}:
                            </span>{" "}
                            <span className="truncate">{p.name}</span>
                          </div>
                          <button
                            type="button"
                            aria-label={`${labels.removePhoto} ${p.name}`}
                            onClick={() => removePhoto(i)}
                            disabled={isPreparingPhotos || isSubmitting}
                            className="shrink-0 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-white/80 dark:hover:bg-white/10 sm:text-sm"
                          >
                            {labels.removePhoto}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-slate-500 dark:text-white/45">{t("form.photoHint")}</p>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting || isPreparingPhotos}
                aria-busy={isSubmitting}
                className="mt-2 w-full rounded-2xl bg-[#66C95E] px-5 py-3.5 text-base font-semibold text-white shadow-sm transition hover:bg-[#58B551] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? t("form.submitting") : t("form.submit")}
              </button>

              {submitStatus === "error" && <p role="alert" className="pt-2 text-sm text-red-500">{submitMessage}</p>}
              {submitStatus === "success" && <p role="status" className="pt-2 text-sm text-green-600">{submitMessage}</p>}
              {(!submitStatus || submitStatus === null) && (
                <p className="pt-1 text-xs text-slate-500 dark:text-white/45">* {t("form.required")}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
